"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import { laadFirestore } from "@/lib/firestore-client";
import { SiteSettings, DEFAULT_SETTINGS } from "@/lib/site-settings";

const SiteSettingsContext = createContext<SiteSettings>(DEFAULT_SETTINGS);

export function SiteSettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<SiteSettings>(DEFAULT_SETTINGS);

  useEffect(() => {
    let afgebroken = false;
    (async () => {
      // MOW-PERF-OKT26: eerst de server-route. Die geeft hetzelfde als de directe Firestore-lezing
      // ({ ...DEFAULT_SETTINGS, ...siteSettings/main }), maar zonder de Firestore-SDK (±470 KB
      // geparseerd) in de eerste bundel van elke pagina te hoeven laden voor één document van 3 KB.
      try {
        const res = await fetch("/api/settings");
        if (res.ok) {
          const data = (await res.json()) as Partial<SiteSettings>;
          if (!afgebroken) setSettings({ ...DEFAULT_SETTINGS, ...data });
          return;
        }
      } catch { /* val terug op Firestore */ }

      // Lukt de route niet (offline, serverfout), dan alsnog rechtstreeks uit Firestore in plaats
      // van stilzwijgend de standaardwaarden te tonen — dat maakte elke instelling uit de backend
      // onzichtbaar. De SDK wordt hier pas geladen.
      try {
        const { db, doc, getDoc } = await laadFirestore();
        const snap = await getDoc(doc(db, "siteSettings", "main"));
        if (snap.exists() && !afgebroken) setSettings({ ...DEFAULT_SETTINGS, ...(snap.data() as Partial<SiteSettings>) });
      } catch { /* stil falen — defaults worden gebruikt */ }
    })();
    return () => { afgebroken = true; };
  }, []);

  return (
    <SiteSettingsContext.Provider value={settings}>
      {children}
    </SiteSettingsContext.Provider>
  );
}

export function useSiteSettings(): SiteSettings {
  return useContext(SiteSettingsContext);
}
